import Supercluster from "supercluster";

/**
 * 카페 좌표를 supercluster 로 인덱싱하고, 화면 영역 + 줌으로 query.
 *
 * - 클러스터링 결정은 거리(픽셀 반경) 기반
 *   → hotspot 에서는 깊은 줌까지 자동으로 묶이고, 한적한 지역은 일찍 풀림
 * - 인덱스는 한 번만 빌드 (수천 개도 ms 단위)
 * - viewport(bbox) query 만 매번 호출 → 일정한 성능
 *
 * 향후 서버 추가 시 같은 라이브러리 그대로 서버사이드 인덱싱으로 옮길 수 있음.
 */

/**
 * 카페 배열로 supercluster 인덱스 빌드.
 * @param {Array} cafes  cafe 객체 배열 (id, name, lat, lng 필요)
 * @param {object} options  supercluster 옵션
 * @returns {Supercluster} 인덱스 인스턴스
 */
export function buildClusterIndex(cafes, options = {}) {
  const index = new Supercluster({
    radius: 70,    // 픽셀 반경. 클수록 hotspot 에서 깊은 줌까지 묶임 (60~80 권장)
    maxZoom: 16,   // 이 줌 이상에서는 클러스터링 안 함 (개별 마커만)
    minPoints: 2,  // 클러스터 최소 마커 수
    extent: 512,
    ...options,
  });

  const features = cafes
    .filter((c) => c.lat != null && c.lng != null)
    .map((c) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [c.lng, c.lat], // GeoJSON 표준: [lng, lat] 순서
      },
      properties: {
        cafeId: c.id,
        name: c.name,
        // 원본 cafe 객체 전체 참조 — query 결과에서 그대로 받음
        cafe: c,
      },
    }));

  index.load(features);
  return index;
}

/**
 * 화면 영역(bbox) + 줌에 대한 클러스터/카페 목록 조회.
 *
 * @param {Supercluster} index
 * @param {object} bounds  { west, south, east, north } — 네이버 LatLngBounds 에서 추출
 * @param {number} zoom  현재 줌. supercluster 는 정수 줌을 받음.
 * @returns {Array} GeoJSON Feature 배열
 *   클러스터: properties.cluster === true, point_count, cluster_id
 *   카페:     properties.cafeId, properties.cafe (원본 객체)
 */
export function getClustersInView(index, bounds, zoom) {
  if (!index) return [];
  const bbox = [bounds.west, bounds.south, bounds.east, bounds.north];
  return index.getClusters(bbox, Math.floor(zoom));
}

/**
 * 클러스터를 클릭했을 때 풀리는 정확한 줌 레벨 (그보다 한 단계 깊으면 클러스터 풀림).
 */
export function getClusterExpansionZoom(index, clusterId) {
  if (!index || clusterId == null) return null;
  try {
    return index.getClusterExpansionZoom(clusterId);
  } catch {
    return null;
  }
}

/**
 * 클러스터 안의 모든 카페(leaves) 조회.
 * 필요 시 (예: 클러스터 클릭으로 카페 리스트 보여주는 UI) 사용.
 */
export function getClusterLeaves(index, clusterId, limit = Infinity) {
  if (!index || clusterId == null) return [];
  try {
    return index.getLeaves(clusterId, limit, 0);
  } catch {
    return [];
  }
}
